import { GetReportsByFilterRequest, EventType } from '@inplay/models';
import { DateRange } from 'react-day-picker';
import { useOrganizationContext } from '../service/context/organizationContext';
import { useResponsiveLayout } from './useResponsiveLayout';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  InterventionReportAdvancedFilters,
  InterventionReportsFilter,
  InterventionReportsKebabOption,
  InterventionReportsPendingAction,
} from '../pages/interventionReports/interventionReports.type';
import { validateInterventionReportsForDeletion } from '../pages/interventionReports/interventionReportsHelper';
import { reportService } from '../service/instances';
import { toastV2 } from '../component/toastV2/toastV2';
import { useTranslation } from 'react-i18next';
import { useUserDetailContext } from '../service/context/userDetailContext';
import { SelectedUser } from '../component/userVerificationModal/userVerificationModal.types';
import { InterventionReportModel } from '../models/interventionReportModel';
import { filterBySearchQuery } from '../utils/filterBySearchQuery';
import { useWebsocketSubscription } from './useWebsocketSubscription';
import { filterInterventionReports } from '../utils/filterInterventionReports';
import { hasActiveInterventionAdvancedFilters } from '../utils/hasActiveInterventionAdvancedFilters';

export const useInterventionReports = () => {
  const { t } = useTranslation();

  const { selectedVenueBranch, loading: organizationContextLoading } =
    useOrganizationContext();
  const { userDetail } = useUserDetailContext();
  const { isTablet } = useResponsiveLayout();
  const [selectedInterventionReport, setSelectedInterventionReport] =
    useState<InterventionReportModel | null>(null);
  const [filters, setFilters] = useState<InterventionReportsFilter>({
    searchQuery: '',
    selectedDateRange: { from: new Date(), to: new Date() },
  });
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const abortControllerRef = useRef<AbortController | null>(null);
  const [allInterventionReports, setAllInterventionReports] = useState<
    InterventionReportModel[]
  >([]);
  const [openCreateIntervention, setOpenCreateIntervention] =
    useState<boolean>(false);
  const [isReviewAndEditMode, setIsReviewAndEditMode] = useState(false);

  const [loading, setLoading] = useState(false);
  const [openFilterModal, setOpenFilterModal] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<InterventionReportModel[]>(
    [],
  );
  const [interventionReportForCreate, setInterventionReportForCreate] =
    useState<InterventionReportModel | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showUserVerification, setShowUserVerification] =
    useState<boolean>(false);
  const [verifiedUser, setVerifiedUser] = useState<SelectedUser | null>(null);
  const [showSelectPlayerModal, setShowSelectPlayerModal] =
    useState<boolean>(false);

  const handleOnVerify = (user: SelectedUser) => {
    setVerifiedUser(user);
    setShowUserVerification(false);

    switch (pendingAction) {
      case InterventionReportsPendingAction.CREATE_INTERVENTION_REPORT:
        setOpenCreateIntervention(true);
        break;
      case InterventionReportsPendingAction.DELETE_REPORTS:
        setShowDeleteConfirmation(true);
        setPendingAction(null);
        break;
      case InterventionReportsPendingAction.EDIT_REPORT:
        setOpenCreateIntervention(true);
        setPendingAction(null);
        break;
      default:
        break;
    }
  };

  const handleOnProceed = () => {
    setShowSelectPlayerModal(false);
    switch (pendingAction) {
      case InterventionReportsPendingAction.CREATE_INTERVENTION_REPORT:
        setOpenCreateIntervention(true);
        setPendingAction(null);
        break;
      default:
        break;
    }
  };

  const handleOnChangeSearch = (query: string) => {
    console.log('[useInterventionReports][handleOnChangeSearch] query', {
      query,
    });
    setFilters((previousFilter) => ({ ...previousFilter, searchQuery: query }));
  };

  const handleReportInterventionClick = () => {
    setPendingAction(
      InterventionReportsPendingAction.CREATE_INTERVENTION_REPORT,
    );
    setShowUserVerification(true);
  };

  const handleReportInterventionClose = () => {
    setOpenCreateIntervention(false);
    setVerifiedUser(null);
    setInterventionReportForCreate(null);
  };

  const handleClearSearch: () => void = (): void => {
    setFilters((previousFilter: InterventionReportsFilter) => ({
      ...previousFilter,
      searchQuery: '',
    }));
  };

  const handleClearFilters: () => void = (): void => {
    setFilters((previousFilter: InterventionReportsFilter) => ({
      searchQuery: previousFilter.searchQuery,
      selectedDateRange: previousFilter.selectedDateRange,
    }));
    setOpenFilterModal(false);
  };

  const fetchInterventionReports = useCallback(
    async (
      override?: Partial<InterventionReportsFilter>,
      fromSocket?: boolean,
    ) => {
      if (!selectedVenueBranch) return;

      abortControllerRef.current?.abort();
      const abortController: AbortController = new AbortController();
      abortControllerRef.current = abortController;

      // Loader ownership is asymmetric:
      //  - ON only for user-initiated requests (no flash on silent refetch)
      //  - OFF whenever the current (non-aborted) request settles, regardless
      //    of owner — otherwise a silent socket fetch that supersedes a user
      //    fetch would leave the loader stuck ON.
      const shouldShowLoader: boolean = !fromSocket;

      try {
        const filtersToUse: InterventionReportsFilter = {
          ...filtersRef.current,
          ...override,
        };
        if (shouldShowLoader) setLoading(true);
        const reportFilterRequest: GetReportsByFilterRequest = {
          venueBranchId: selectedVenueBranch._id,
          currentDate: filtersToUse.selectedDateRange.from ?? new Date(),
          startDate: filtersToUse.selectedDateRange.from,
          endDate:
            filtersToUse.selectedDateRange.to ??
            filtersToUse.selectedDateRange.from,
        };
        const interventionReportsFromApi: InterventionReportModel[] =
          await reportService.getInterventionReports(
            reportFilterRequest,
            abortController.signal,
          );
        if (abortController.signal.aborted) return;
        setAllInterventionReports(interventionReportsFromApi);
      } catch (error) {
        if (abortController.signal.aborted) return;
        if (fromSocket) {
          console.error(
            '[useInterventionReports] silent refetch failed',
            error,
          );
        } else {
          console.error('Error fetching intervention reports:', error);
          toastV2.error(t('interventionReports.toast.fetchError'));
        }
      } finally {
        if (!abortController.signal.aborted) setLoading(false);
      }
    },
    [selectedVenueBranch],
  );

  const filteredReports: InterventionReportModel[] = useMemo(
    () => filterInterventionReports(allInterventionReports, filters),
    [allInterventionReports, filters],
  );

  const interventionReports: InterventionReportModel[] = useMemo(
    () =>
      filterBySearchQuery(
        filteredReports,
        filters.searchQuery,
        (model: InterventionReportModel) => model.searchableFields,
      ),
    [filteredReports, filters.searchQuery],
  );

  const activeFiltersExist: boolean = useMemo(
    () => hasActiveInterventionAdvancedFilters(filters),
    [filters],
  );

  useWebsocketSubscription(
    selectedVenueBranch?._id,
    [EventType.INTERVENTION],
    () => fetchInterventionReports(undefined, true),
  );

  const handleDateRangeChange = (range: DateRange) => {
    const selectedDateRange: DateRange = {
      from: range.from,
      to: range.to ?? range.from,
    };
    setFilters((prev) => ({ ...prev, selectedDateRange }));
    fetchInterventionReports({ selectedDateRange });
  };

  const toggleFilterModal = () => setOpenFilterModal((prev) => !prev);

  const handleApplyFilters = (
    advancedFilters: InterventionReportAdvancedFilters,
    newDateRange?: DateRange,
  ): void => {
    const currentFilters: InterventionReportsFilter = filtersRef.current;
    const updatedFilters: InterventionReportsFilter = {
      ...currentFilters,
      ...advancedFilters,
    };

    if (newDateRange) {
      updatedFilters.selectedDateRange = newDateRange;
    }

    setFilters(updatedFilters);
    setOpenFilterModal(false);

    const dateChanged: boolean = Boolean(
      newDateRange &&
      (newDateRange.from?.getTime() !==
        currentFilters.selectedDateRange?.from?.getTime() ||
        newDateRange.to?.getTime() !==
        currentFilters.selectedDateRange?.to?.getTime()),
    );

    if (dateChanged) {
      fetchInterventionReports({ selectedDateRange: newDateRange });
    }
  };

  const handleSelectionChange = (rows: InterventionReportModel[]) => {
    setSelectedRows(rows);
  };

  const handleRowClick = (row: InterventionReportModel) => {
    setSelectedInterventionReport(row);
  };

  const handleKebabActionFromKebabMenu = async (
    action: InterventionReportsKebabOption,
    row: InterventionReportModel,
    rowIndex: number,
  ): Promise<void> => {
    console.log(
      '[useInterventionReports][handleKebabActionFromKebabMenu] ENTRY',
      {
        action,
        row,
        rowIndex,
      },
    );

    // TODO: Add each functionality here
    switch (action) {
      case InterventionReportsKebabOption.VIEW:
        setSelectedInterventionReport(row);
        return;
      case InterventionReportsKebabOption.EDIT:
        setInterventionReportForCreate(row);
        setPendingAction(InterventionReportsPendingAction.EDIT_REPORT);
        setShowUserVerification(true);
        setIsReviewAndEditMode(false);
        return;
      case InterventionReportsKebabOption.REVIEW_AND_SIGN:
        setInterventionReportForCreate(row);
        setPendingAction(InterventionReportsPendingAction.EDIT_REPORT);
        setShowUserVerification(true);
        setIsReviewAndEditMode(true);
        return;
      case InterventionReportsKebabOption.DOWNLOAD:
      case InterventionReportsKebabOption.HISTORY:
        toastV2.warning(t('interventionReports.toast.featureNotAvailable'));
        return;
      case InterventionReportsKebabOption.DELETE:
        setSelectedRows([row]);
        setPendingAction(InterventionReportsPendingAction.DELETE_REPORTS);
        setShowUserVerification(true);
        return;
      default: {
        console.error('Invalid Intervention Report Kebab Option');
        const _exhaustiveCheck: never = action;
        return _exhaustiveCheck;
      }
    }
  };

  const handleReviewAndSignClick = () => {
    if (!selectedInterventionReport) return;
    setInterventionReportForCreate(selectedInterventionReport);
    setPendingAction(InterventionReportsPendingAction.EDIT_REPORT);
    setShowUserVerification(true);
    setIsReviewAndEditMode(true);
    setSelectedInterventionReport(null);
  };
  const closeInterventionReportDetails = () => {
    setSelectedInterventionReport(null);
  };

  useEffect(() => {
    fetchInterventionReports();
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [selectedVenueBranch]);

  const handleDeleteClick = () => {
    setPendingAction(InterventionReportsPendingAction.DELETE_REPORTS);
    setShowUserVerification(true);
  };

  const handleDeleteInterventionReports = async () => {
    try {
      if (!selectedRows.length) return;

      const { valid, invalid } = validateInterventionReportsForDeletion(
        selectedRows.map((row: InterventionReportModel) => row.raw),
        userDetail,
      );

      if (invalid.length > 0) {
        toastV2.error(invalid[0]);
        setShowDeleteConfirmation(false);
        setSelectedRows([]);
        return;
      }

      if (!valid.length) {
        toastV2.error(t('interventionReports.toast.noValidReports'));
        setShowDeleteConfirmation(false);
        setSelectedRows([]);
        return;
      }

      const originalReportIds = valid
        .map((row) => row.originalReportId)
        .filter((id): id is string => Boolean(id));

      if (!originalReportIds.length) {
        toastV2.error(t('interventionReports.toast.noValidIds'));
        setShowDeleteConfirmation(false);
        setSelectedRows([]);
        return;
      }

      setDeleting(true);
      const response =
        await reportService.deleteInterventionReports(originalReportIds);
      const count = response.deletedInterventionReportsCount;

      toastV2.success(
        t('interventionReports.toast.deleteSuccess', {
          report:
            count === 1
              ? t('interventionReports.singleReport')
              : t('interventionReports.multipleReports'),
        }),
      );

      setSelectedRows([]);
      setShowDeleteConfirmation(false);
      await fetchInterventionReports(filters);
    } catch (error) {
      console.log('selectedRows', { selectedRows });
      console.error('Error deleting intervention reports:', error);
      toastV2.error(
        t('interventionReports.toast.deleteError', {
          report:
            selectedRows.length === 1
              ? t('interventionReports.singleReport')
              : t('interventionReports.multipleReports'),
        }),
      );
    } finally {
      setDeleting(false);
    }
  };

  return {
    selectedVenueBranch,
    isTablet,
    organizationContextLoading,
    fetchInterventionReports,
    handleClearFilters,
    handleClearSearch,
    handleReportInterventionClose,
    handleReportInterventionClick,
    handleKebabActionFromKebabMenu,
    handleOnChangeSearch,
    handleDateRangeChange,
    loading,
    selectedInterventionReport,
    InterventionReports: interventionReports,
    filters,
    openFilterModal,
    toggleFilterModal,
    handleApplyFilters,
    activeFiltersExist,
    openCreateIntervention,
    isReviewAndEditMode,
    selectedRows,
    handleSelectionChange,
    handleRowClick,
    interventionReportForCreate,
    t,
    handleReviewAndSignClick,
    closeInterventionReportDetails,
    setSelectedRows,
    handleDeleteClick,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    handleDeleteInterventionReports,
    deleting,
    showUserVerification,
    setShowUserVerification,
    handleOnVerify,
    handleOnProceed,
    setShowSelectPlayerModal,
    showSelectPlayerModal,
    verifiedUser,
  };
};

export const useDownloadInterventionReport = (): {
  downloadInterventionReportLoading: boolean;
  bulkDownloadLoading: boolean;
  setBulkDownloadLoading: Dispatch<SetStateAction<boolean>>;
  setDownloadInterventionReportLoading: Dispatch<SetStateAction<boolean>>;
} => {
  const [
    downloadInterventionReportLoading,
    setDownloadInterventionReportLoading,
  ] = useState<boolean>(false);
  const [bulkDownloadLoading, setBulkDownloadLoading] =
    useState<boolean>(false);

  return {
    setDownloadInterventionReportLoading,
    downloadInterventionReportLoading,
    setBulkDownloadLoading,
    bulkDownloadLoading,
  };
};
